import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { toastApiError } from '../lib/apiToast';

export type PreviewTarget = {
  docId: string;
  fileId: string;
  name: string;
  contentType: string;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'error' };

// Fetches a fresh signed URL on open so links never expire while the modal
// is mounted. Closes on Escape, backdrop click, or the × button. Portaled to
// <body> so transformed ancestors (card animations) can't trap `fixed`.
export const DocumentPreviewModal: React.FC<{
  target: PreviewTarget;
  onClose: () => void;
}> = ({ target, onClose }) => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    void api
      .get<{ url: string; expires_in: number }>(
        `/admin/documents/${target.docId}/files/${target.fileId}/url`,
      )
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data?.url) {
          setState({ kind: 'ready', url: res.data.url });
        } else {
          toastApiError(res, 'Failed to load document preview');
          setState({ kind: 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [target.docId, target.fileId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Document preview"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl max-h-full flex flex-col rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-900 truncate">{target.name}</div>
          <div className="flex items-center gap-3 shrink-0">
            {state.kind === 'ready' && (
              <a
                href={state.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-600 underline hover:text-gray-900"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              autoFocus
              className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-1"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4">
          {state.kind === 'loading' && (
            <div aria-busy="true">
              <span className="block h-64 w-full shimmer" />
            </div>
          )}
          {state.kind === 'error' && (
            <div className="text-sm text-red-700">Could not load preview.</div>
          )}
          {state.kind === 'ready' &&
            (target.contentType.startsWith('image/') ? (
              <img
                src={state.url}
                alt={target.name}
                className="max-w-full max-h-[75vh] mx-auto rounded"
              />
            ) : target.contentType === 'application/pdf' ? (
              <iframe
                src={state.url}
                title={target.name}
                className="w-full h-[75vh] border border-gray-200 rounded"
              />
            ) : (
              <a
                href={state.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline text-sm"
              >
                Open file in new tab
              </a>
            ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};
